/**
 * Journal model (task 20.3, R7.1 / R2.3).
 *
 * R7.1 requires the frontend to retain the FULL, unabridged chat log for player
 * review. The journal is that log: an append-only, ordered list of entries the
 * play view renders in full (scrollable, complete — nothing is dropped or
 * summarized client-side). Every asynchronous signal the play view receives
 * folds into a journal entry:
 *
 *   - free-flow chat messages (R31.2) and DM interjections → chat entries,
 *   - committed turn results → a narration entry,
 *   - in-app / reported rolls surface as chat/audit entries (the roll is
 *     recorded in the campaign chat audit trail, R2.3 — the server appends it
 *     and echoes it back over the free-flow push, which is the single source
 *     folded here; the SPA emits no separate local roll echo),
 *   - retry signals → a transient system note (the retry STATE is tracked
 *     separately by the play view; here we keep an audit line so the log stays
 *     complete).
 *
 * The writing indicator is NOT a journal entry — it is transient UI state — so
 * it is handled by the play view, not folded here.
 *
 * This module is pure (no React, no I/O) so the full-log behavior is unit
 * tested directly.
 */

import type { MessageMode } from "@rufus/shared-types";
import {
  FREE_FLOW_MESSAGE_PUSH_TYPE,
  DM_INTERJECTION_PUSH_TYPE,
  COLD_START_WAITING_PUSH_TYPE,
  RECAP_PUSH_TYPE,
  SYSTEM_AUDIT_PUSH_TYPE,
  type RufusPush,
} from "./push-types.js";

/** The kind of a journal entry, for rendering + filtering. */
export type JournalEntryKind = "chat" | "dm" | "narration" | "roll" | "system";

/**
 * The stable journal id for the "previously, in your campaign…" PRELUDE line,
 * keyed per campaign. BOTH the server's `session_recap` push (fired on a
 * returning connection past the away-gap) AND the client's hydration summary
 * header render the campaign's rolling summary, so they MUST share this id —
 * otherwise a returning player who crosses the recap boundary sees the same
 * "previously…" block twice (one from each source). Sharing the id means
 * whichever lands second replaces the first in place (the richer server recap
 * wins when it arrives; the hydration header stands alone when no recap fires).
 */
export function preludeId(campaignId: string): string {
  return `prelude:${campaignId}`;
}

/** One immutable line in the full, unabridged journal (R7.1). */
export interface JournalEntry {
  /** Stable id for React keys / dedupe. */
  readonly id: string;
  readonly kind: JournalEntryKind;
  /** Who authored it (character/user id or "Rufus"), if known. */
  readonly author?: string | null;
  /**
   * Whether a player chat line was in character or out of character (OOC/IC
   * axis). Present only for an explicit `ooc` line; absent ⇒ in character. The
   * Journal uses it to pick the speaker label: the player's display name for an
   * OOC aside vs the character name for an IC line.
   */
  readonly mode?: MessageMode;
  /** The rendered text of the entry. */
  readonly text: string;
  /** Monotonic conversation-log seq when the entry carries one (chat/roll). */
  readonly seq?: number;
  /** Turn id for narration / retry entries. */
  readonly turnId?: string;
  /** ISO timestamp if the source carried one. */
  readonly timestamp?: string;
}

/**
 * Fold a single push into a journal entry, or `null` when the push is not a
 * journal event (e.g. a writing indicator — transient UI state, not a log line).
 */
export function pushToJournalEntry(push: RufusPush): JournalEntry | null {
  switch (push.type) {
    case FREE_FLOW_MESSAGE_PUSH_TYPE:
      return {
        id: `chat:${push.seq}`,
        kind: "chat",
        author: push.authorId ?? push.role,
        text: push.text,
        seq: push.seq,
        timestamp: push.timestamp,
        ...(push.mode !== undefined ? { mode: push.mode } : {}),
      };
    case DM_INTERJECTION_PUSH_TYPE:
      return {
        id: push.seq != null ? `dm:${push.seq}` : `dm:${push.text}`,
        kind: "dm",
        author: "Rufus",
        text: push.text,
        seq: push.seq,
        timestamp: push.timestamp,
      };
    case RECAP_PUSH_TYPE:
      // A session "previously…" recap (§3.5b) — a DM-voiced journal line. Keyed
      // on the shared PRELUDE id (per campaign) so a re-delivered recap on a
      // flapping reconnect dedupes in place AND so it MERGES with the client
      // hydration summary header (which uses the same id) rather than rendering a
      // second "previously…" block (round-journal-review Medium #2). The server
      // recap is the richer of the two (it adds location + open threads), so
      // when it arrives it replaces the plain hydration header in place.
      return {
        id: preludeId(push.campaignId),
        kind: "dm",
        author: "Rufus",
        text: push.text,
      };
    case SYSTEM_AUDIT_PUSH_TYPE:
      // A committed ledger/campaign audit line, fanned out live. Keyed on `seq`
      // as `chat:${seq}` — IDENTICAL to the id `historyEntryToJournalEntry`
      // assigns a `system`-role history entry — so this live push and a later
      // re-hydration of the same entry MERGE in place (no double render), exactly
      // like the free-flow chat path.
      return {
        id: `chat:${push.seq}`,
        kind: "system",
        author: push.authorId ?? push.role,
        text: push.text,
        seq: push.seq,
        timestamp: push.timestamp,
      };
    case "result":
      return {
        id: `narration:${push.turnId}`,
        kind: "narration",
        author: "Rufus",
        text: push.narrative,
        turnId: push.turnId,
      };
    case "retry":
      // The retry STATE is surfaced separately by the play view; we keep an
      // audit line so the full log records that a retry occurred (R7.1).
      return {
        id: `retry:${push.turnId}`,
        kind: "system",
        text: `Turn could not be completed: ${push.reason}`,
        turnId: push.turnId,
      };
    case "writing_indicator":
      return null;
    case COLD_START_WAITING_PUSH_TYPE:
      // The cold-start waiting cue ("Rufus consults the archives…") is transient
      // UI state, not a log line — the play view tracks it separately.
      return null;
    default:
      return null;
  }
}

/**
 * Convert a server journal-history entry (the hydration read on Play-screen
 * load) into a {@link JournalEntry}. The `id` scheme MUST match the one
 * {@link pushToJournalEntry} assigns to the live push for the same line, so a
 * replayed history entry and a later live WebSocket push for the SAME `seq`
 * MERGE in place via {@link appendJournalEntry} rather than double-rendering:
 *
 *   - a `player` line ⇒ `chat:${seq}` (the free-flow push's id);
 *   - an `ai_pc` line is likewise a conversation line keyed on seq ⇒ `chat:${seq}`;
 *   - a `dm` line ⇒ `dm:${seq}` (the DM-interjection push's seq-keyed id);
 *   - a `system`/audit line ⇒ `chat:${seq}` (it is a seq-bearing log line; roll
 *     audit lines fan back over the free-flow push on the same seq).
 *
 * Narration (`result`) entries are keyed by turnId, not seq, and are NOT part of
 * the conversation-log history read — they arrive only over the live push — so
 * there is nothing to reconcile for them here.
 */
export function historyEntryToJournalEntry(entry: {
  readonly seq: number;
  readonly role: "player" | "dm" | "ai_pc" | "system";
  readonly authorId: string | null;
  readonly text: string;
  readonly mode?: MessageMode;
  readonly timestamp: number;
}): JournalEntry {
  const iso = new Date(entry.timestamp).toISOString();
  if (entry.role === "dm") {
    return {
      id: `dm:${entry.seq}`,
      kind: "dm",
      author: "Rufus",
      text: entry.text,
      seq: entry.seq,
      timestamp: iso,
    };
  }
  // player / ai_pc / system → a seq-keyed conversation line (matches the
  // free-flow push id `chat:${seq}`).
  return {
    id: `chat:${entry.seq}`,
    kind: entry.role === "system" ? "system" : "chat",
    author: entry.authorId ?? entry.role,
    text: entry.text,
    seq: entry.seq,
    timestamp: iso,
    ...(entry.mode !== undefined ? { mode: entry.mode } : {}),
  };
}

/**
 * Order the journal for display after a hydration merge (round-journal-review
 * Medium #1). Hydration can resolve AFTER a live push has already populated the
 * journal (both the socket and the HTTP read start on mount), and a plain append
 * would then render the replayed OLD history + the "previously…" header BELOW
 * the newer live line. This re-orders deterministically:
 *   - the PRELUDE header (`prelude:${campaignId}`) is pinned to the very top;
 *   - every seq-bearing entry (chat/dm/system history + live free-flow pushes)
 *     is ordered by `seq` ascending — the authoritative conversation order;
 *   - entries WITHOUT a seq (a seq-less DM interjection, a `narration:`/`retry:`
 *     entry keyed by turnId) keep their arrival order and sort AFTER the
 *     seq-bearing block, i.e. they stay at the bottom as the newest lines.
 * A stable sort preserves relative order within each group. Pure + idempotent.
 */
function orderJournal(journal: readonly JournalEntry[]): readonly JournalEntry[] {
  const rank = (e: JournalEntry): number => {
    if (e.id.startsWith("prelude:")) return -1; // pinned top
    return e.seq ?? Number.MAX_SAFE_INTEGER; // seq-less → bottom (newest)
  };
  // `Array.prototype.sort` is stable in modern engines, so entries sharing a
  // rank (e.g. several seq-less live entries) keep their arrival order.
  return journal
    .map((e, i) => ({ e, i }))
    .sort((a, b) => {
      const ra = rank(a.e);
      const rb = rank(b.e);
      return ra !== rb ? ra - rb : a.i - b.i;
    })
    .map((x) => x.e);
}

/**
 * Seed the journal from a hydration read: a player-safe `summary` (prepended as
 * a single "previously…" PRELUDE entry when non-empty) plus the replayed
 * conversation entries. Each entry is merged by id (so re-hydrating, or a later
 * live push for the same seq, replaces in place), and the result is re-ordered
 * by seq so replayed history and the header land in the right place even when a
 * live push populated the journal first (round-journal-review Medium #1).
 *
 * The prelude header shares its id with the server session recap
 * ({@link preludeId}), so the two never render as two "previously…" blocks
 * (Medium #2) — whichever arrives second replaces the first.
 */
export function hydrateJournal(
  journal: readonly JournalEntry[],
  campaignId: string,
  summary: string,
  entries: readonly {
    readonly seq: number;
    readonly role: "player" | "dm" | "ai_pc" | "system";
    readonly authorId: string | null;
    readonly text: string;
    readonly mode?: MessageMode;
    readonly timestamp: number;
  }[],
): readonly JournalEntry[] {
  let next = journal;
  const trimmedSummary = summary.trim();
  if (trimmedSummary.length > 0) {
    // Only seed the hydration header if a (richer) server recap has not ALREADY
    // claimed the shared prelude id — don't overwrite the recap's location/
    // open-threads detail with the plainer summary-only header.
    const hasRecap = next.some((e) => e.id === preludeId(campaignId));
    if (!hasRecap) {
      next = appendJournalEntry(next, {
        id: preludeId(campaignId),
        kind: "dm",
        author: "Rufus",
        text: `Previously, in your campaign:\n${trimmedSummary}`,
      });
    }
  }
  for (const e of entries) {
    next = appendJournalEntry(next, historyEntryToJournalEntry(e));
  }
  return orderJournal(next);
}

/**
 * Append an entry to the journal, preserving the full, unabridged log while
 * de-duplicating by `id` (the same push can arrive twice on a reconnect). The
 * log is NEVER trimmed — R7.1 requires the complete history for review. When an
 * entry with an existing id arrives again it replaces the prior copy in place
 * (a re-delivered narration/result), keeping order stable.
 */
export function appendJournalEntry(
  journal: readonly JournalEntry[],
  entry: JournalEntry,
): readonly JournalEntry[] {
  const existingIndex = journal.findIndex((e) => e.id === entry.id);
  if (existingIndex >= 0) {
    const next = journal.slice();
    next[existingIndex] = entry;
    return next;
  }
  return [...journal, entry];
}

// NOTE: there are deliberately NO optimistic local-echo helpers. The play view
// does not emit a local entry for a chat line or a roll — the server fans both
// back over the free-flow push keyed by the authoritative conversation-log
// `seq`, which is the single source folded here. A local echo would carry no
// server `seq` to dedupe against and would double-render once the push lands.
